import { ArrowRight, Download, FileText, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { PACKAGES, formatPrice } from "@/data/packages";

const PDF_PATH = "/resources/equality-action-plan-template.pdf";
const DOCX_PATH = "/resources/equality-action-plan-template.docx";
const PAGE_PATH = "/resources/equality-action-plan-template";
const CHECKED = "4 November 2026";
const pkg = PACKAGES.find((p) => p.id === "equality-action-plan");

const SOURCES = [
  { label: "Equality Act 2010, section 78A (equality action plans)", url: "https://www.legislation.gov.uk/ukpga/2010/15/section/78A" },
  { label: "Employment Rights Act 2025, section 33 (in force 6 April 2026)", url: "https://www.legislation.gov.uk/ukpga/2025/36/section/33" },
  { label: "GOV.UK: Creating an action plan: guidance for employers", url: "https://www.gov.uk/government/publications/creating-an-action-plan-guidance-for-employers" },
  { label: "GOV.UK: Step 2, choose your actions (the 18 recommended actions)", url: "https://www.gov.uk/government/publications/creating-an-action-plan-guidance-for-employers/step-2-choose-your-actions" },
];

const Para = ({ children }) => <p className="mt-4 text-base leading-relaxed text-ink/70">{children}</p>;
const A = ({ to, children }) => <Link to={to} className="font-semibold underline decoration-pink decoration-2 underline-offset-4">{children}</Link>;

export default function ResourceEqualityActionPlan() {
  const origin = window.location.origin;
  const pageUrl = `${origin}${PAGE_PATH}`;
  return (
    <main data-testid="equality-action-plan-page">
      <Seo
        title="Equality Action Plan Template: Gender Pay Gap and Menopause | Electra HR"
        siteName="Electra HR"
        description="Free equality action plan template for employers with 250+ staff: gender pay gap and menopause actions, the 18 government actions, word limits and deadlines. PDF and DOCX."
        image="/og/default.jpg"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "WebPage", "@id": pageUrl, url: pageUrl, name: "Equality action plan template", isPartOf: { "@id": `${origin}/#website` }, publisher: { "@id": `${origin}/#organization` }, datePublished: "2026-11-04", dateModified: "2026-11-04" },
            { "@type": "DigitalDocument", name: "Equality action plan template", url: `${origin}${PDF_PATH}`, encodingFormat: "application/pdf", author: { "@id": `${origin}/#organization` } },
            { "@type": "DigitalDocument", name: "Equality action plan template, editable DOCX", url: `${origin}${DOCX_PATH}`, encodingFormat: "application/vnd.openxmlformats-officedocument.wordprocessingml.document", author: { "@id": `${origin}/#organization` } },
            { "@type": "BreadcrumbList", itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: origin },
              { "@type": "ListItem", position: 2, name: "Equality action plan template", item: pageUrl },
            ] },
          ],
        }}
      />

      <section className="grain bg-ink pb-16 pt-32 sm:pt-40 text-paper">
        <div className="container-x max-w-4xl">
          <p className="eyebrow text-pink">Free employer template</p>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
            Equality action plan template: gender pay gap and menopause
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-paper/65">
            For employers with 250 or more staff. Prepare your plan before you open the gender pay gap service: the actions, the word limits, the sign-off and the deadlines in one document.
          </p>
          <p className="mt-5 font-mono text-xs uppercase tracking-[0.18em] text-paper/45">Law and guidance checked {CHECKED}</p>
        </div>
      </section>

      <section className="bg-paper py-16 sm:py-24">
        <div className="container-x grid gap-10 lg:grid-cols-[1fr_380px]">
          <div>
            <Reveal>
              <div data-testid="eap-short-answer" className="border-l-4 border-pink bg-pink/10 p-6">
                <p className="text-base font-medium leading-relaxed text-ink/80">
                  <strong>Short answer:</strong> employers with 250 or more employees can publish an equality action plan now, voluntarily, by 4 April 2027 (30 March 2027 for most public authorities). It needs at least one action on the gender pay gap and one supporting employees experiencing menopause. The government says plans will become mandatory from spring 2027, subject to legislation: the power is in section 78A of the Equality Act 2010, but the duty only applies once regulations are made, and none had been made when we checked on {CHECKED}.
                </p>
              </div>
            </Reveal>

            <div className="mt-10 grid gap-5 sm:grid-cols-2">
              <a href={PDF_PATH} download data-testid="download-eap-pdf" className="group border-2 border-ink bg-ink p-7 text-paper transition-all duration-300 hover:-translate-y-1 hover:shadow-[6px_6px_0_0_#F472B6]">
                <Download size={24} className="text-pink" />
                <p className="mt-5 font-display text-xl font-semibold">Download the PDF</p>
                <p className="mt-2 text-sm leading-relaxed text-paper/65">For printing and for the sign-off meeting.</p>
              </a>
              <a href={DOCX_PATH} download data-testid="download-eap-docx" className="group border-2 border-ink bg-paper p-7 text-ink transition-all duration-300 hover:-translate-y-1 hover:shadow-[6px_6px_0_0_#0F0F1A]">
                <FileText size={24} className="text-pink" />
                <p className="mt-5 font-display text-xl font-semibold">Download the DOCX</p>
                <p className="mt-2 text-sm leading-relaxed text-ink/65">Editable, to draft your actions and narrative.</p>
              </a>
            </div>
            <p className="mt-4 text-sm text-ink/55">No form and no email gate. Electra HR does not receive or store completed copies.</p>

            <div className="mt-12 space-y-10">
              <section>
                <h2 className="font-display text-2xl font-semibold tracking-tight">Who needs an equality action plan?</h2>
                <Para>Employers with 250 or more employees, the same group that already reports its gender pay gap. In a group of companies, each legal entity with 250 or more employees needs its own plan, although you can copy the same actions and narrative across them. Private and voluntary sector plans must name a responsible person, usually a director, partner or senior officer, who confirms the information is accurate.</Para>
                <Para>Smaller employers are outside the duty, but the same actions are good practice. Our <A to="/equality-at-work/employers">equality at work hub for employers</A> covers the rules that apply to everyone.</Para>
              </section>

              <section>
                <h2 className="font-display text-2xl font-semibold tracking-tight">What has to go in the plan</h2>
                <ul className="mt-5 space-y-3 text-base leading-relaxed text-ink/70">
                  {[
                    "At least 2 actions from the government's list of 18: at least one on the gender pay gap and at least one supporting employees experiencing menopause",
                    "At least 2 of them marked 'new or in progress'; actions already part of how you work can be added as 'embedded'",
                    "For each new or in-progress action: why you chose it and how you will track whether it works, in up to 100 words, with no links or attachments",
                    "An optional overall narrative of up to 200 words, published alongside your gender pay gap figures",
                    "A link to the page on your own website where the plan also appears",
                  ].map((item) => (
                    <li key={item} className="flex gap-3"><CheckCircle2 size={18} className="mt-1 shrink-0 text-pink" /><span>{item}</span></li>
                  ))}
                </ul>
              </section>

              <section>
                <h2 className="font-display text-2xl font-semibold tracking-tight">The menopause actions</h2>
                <Para>Six of the 18 actions are about menopause: training managers, occupational health advice, support groups and networks, workplace adjustments, a menopause risk assessment, and reviewing policies and procedures. The template lists all 18 so you can tick the ones that fit. Menopause symptoms can also engage disability and sex discrimination protection, so adjustments are worth getting right whatever the plan says. Our <A to="/equality-at-work/is-it-protected">is it protected? checker</A> explains when.</Para>
              </section>

              <section>
                <h2 className="font-display text-2xl font-semibold tracking-tight">Deadlines</h2>
                <Para>For the 2026 to 2027 reporting year, voluntary plans can be published up to 4 April 2027 for private, voluntary and most other employers, and 30 March 2027 for most public authorities. Plans are submitted on the GOV.UK gender pay gap service alongside your gender pay gap figures. If regulations make plans compulsory, they will set the dates, and our <A to="/law-changes">law changes page</A> will list them when they are made.</Para>
              </section>

              <section>
                <h2 className="font-display text-2xl font-semibold tracking-tight">Sources</h2>
                <ul className="mt-4 space-y-2 text-sm">
                  {SOURCES.map((s) => (
                    <li key={s.url}><a href={s.url} target="_blank" rel="noopener noreferrer" className="underline decoration-pink decoration-2 underline-offset-4 hover:text-pink">{s.label}</a></li>
                  ))}
                </ul>
              </section>
            </div>
          </div>

          <aside className="h-fit border-2 border-ink bg-pink/10 p-7 lg:sticky lg:top-28">
            <p className="eyebrow text-ink/50">Done for you</p>
            <p className="mt-4 font-display text-xl font-semibold">We'll draft your action plan</p>
            <p className="mt-3 text-sm leading-relaxed text-ink/70">{pkg.detail}. Ready for your responsible person to approve and submit.</p>
            <p className="mt-5 font-display text-2xl font-semibold">{formatPrice(pkg)}</p>
            <p className="mt-1 font-mono text-xs uppercase tracking-[0.18em] text-ink/45">{pkg.turnaround}</p>
            <Link to="/contact?need=equality-action-plan" data-testid="eap-package-cta" className="group mt-6 inline-flex items-center gap-2 bg-ink px-5 py-3 font-bold text-paper transition-transform duration-300 hover:-translate-y-0.5">
              Get a fixed quote <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <p className="mt-6 border-t border-ink/15 pt-5 text-xs leading-relaxed text-ink/55">
              General information, not legal advice on your situation. Electra HR Ltd, company number 11077627.
            </p>
          </aside>
        </div>
      </section>
    </main>
  );
}
